/**
 * Column Configuration Service
 * Manages column configurations with API integration
 * Max 5 configurations per user per form
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { COLUMN_CONFIGURATION_ENDPOINTS } from '../endpoints';

/**
 * @typedef {Object} CreateColumnConfigDto
 * @property {string} name - Config name (1-100 chars, required)
 * @property {number} formSchemaId - Form schema ID (required)
 * @property {string} [description] - Optional description (max 500 chars)
 * @property {Array<string>} visibleColumns - Array of column keys in order (required)
 * @property {Object<string, number>} [columnSizing] - Map of column key to width in pixels
 * @property {boolean} [isShared] - Share with organization
 * @property {boolean} [isDefault] - Set as default config
 */

/**
 * @typedef {Object} UpdateColumnConfigDto
 * @property {string} [name] - New name (1-100 chars)
 * @property {string} [description] - New description (max 500 chars)
 * @property {Array<string>} [visibleColumns] - Updated visible columns
 * @property {Object<string, number>} [columnSizing] - Updated column sizing
 * @property {boolean} [isShared] - Update share status
 * @property {boolean} [isDefault] - Update default status
 */

/**
 * @typedef {Object} ColumnConfigListDto
 * @property {number} id - Configuration ID
 * @property {number} formSchemaId - Form schema ID
 * @property {string} name - Configuration name
 * @property {string} [description] - Description
 * @property {boolean} isShared - Is shared with organization
 * @property {boolean} isDefault - Is user's default config
 * @property {boolean} isOwner - Is current user the owner (use this instead of userId)
 * @property {string} ownerName - Owner display name
 * @property {string} createdAt - ISO timestamp
 */

/**
 * @typedef {Object} ColumnConfigResponseDto
 * @property {number} id - Configuration ID
 * @property {string} name - Configuration name
 * @property {number} formSchemaId - Form schema ID
 * @property {string} [description] - Description
 * @property {Array<string>} visibleColumns - Visible column keys
 * @property {Object<string, number>} columnSizing - Column sizing map
 * @property {boolean} isDefault - Is user's default config
 * @property {boolean} isShared - Is shared with organization
 * @property {boolean} isOwner - Is current user the owner
 * @property {number} userId - Owner user ID
 * @property {string} ownerName - Owner display name
 * @property {string} createdAt - ISO timestamp
 * @property {string} updatedAt - ISO timestamp
 */

// Maximum configurations per user per form
const MAX_CONFIGS_PER_USER = 5;

const columnConfigurationService = {
  MAX_CONFIGS_PER_USER,

  /**
   * Get all configurations for a form (user's + shared)
   * @returns {Promise<ColumnConfigListDto[]>}
   */
  getConfigsByFormId: async (formSchemaId) => {
    console.log('[ColumnConfigService] Fetching configs for formSchemaId:', formSchemaId);
    const response = await apiRequest(() =>
      axiosInstance.get(COLUMN_CONFIGURATION_ENDPOINTS.LIST_BY_FORM(formSchemaId))
    );
    console.log('[ColumnConfigService] Raw API response:', response);
    const configs = response.$values || response || [];
    console.log('[ColumnConfigService] Processed configs:', configs);
    return configs;
  },

  /**
   * Get configuration by ID
   */
  getConfigById: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(COLUMN_CONFIGURATION_ENDPOINTS.GET_BY_ID(id))
    );
  },

  /**
   * Get user's default configuration for a form
   * Returns null if no default exists (404)
   */
  getDefaultConfig: async (formSchemaId) => {
    try {
      return await apiRequest(() =>
        axiosInstance.get(COLUMN_CONFIGURATION_ENDPOINTS.GET_DEFAULT(formSchemaId))
      );
    } catch (error) {
      if (error.status === 404) return null;
      throw error;
    }
  },

  /**
   * Create new configuration
   */
  createConfig: async (configData) => {
    // Validation
    if (!configData.name || configData.name.trim().length === 0) {
      throw new Error('Configuration name is required');
    }
    if (configData.name.length > 100) {
      throw new Error('Configuration name must not exceed 100 characters');
    }
    if (!configData.formSchemaId) {
      throw new Error('Form schema ID is required');
    }
    if (!configData.visibleColumns || !Array.isArray(configData.visibleColumns)) {
      throw new Error('Visible columns are required');
    }

    return apiRequest(() =>
      axiosInstance.post(COLUMN_CONFIGURATION_ENDPOINTS.CREATE, {
        formSchemaId: configData.formSchemaId,
        name: configData.name,
        description: configData.description || null,
        visibleColumns: configData.visibleColumns,
        columnSizing: configData.columnSizing || {},
        isShared: configData.isShared || false,
        isDefault: configData.isDefault || false
      })
    );
  },

  /**
   * Update existing configuration (owner only)
   */
  updateConfig: async (id, configData) => {
    if (configData.name !== undefined) {
      if (configData.name.trim().length === 0) {
        throw new Error('Configuration name cannot be empty');
      }
      if (configData.name.length > 100) {
        throw new Error('Configuration name must not exceed 100 characters');
      }
    }

    const payload = {};
    if (configData.name !== undefined) payload.name = configData.name;
    if (configData.description !== undefined) payload.description = configData.description;
    if (configData.visibleColumns !== undefined) payload.visibleColumns = configData.visibleColumns;
    if (configData.columnSizing !== undefined) payload.columnSizing = configData.columnSizing;
    if (configData.isShared !== undefined) payload.isShared = configData.isShared;
    if (configData.isDefault !== undefined) payload.isDefault = configData.isDefault;

    return apiRequest(() =>
      axiosInstance.put(COLUMN_CONFIGURATION_ENDPOINTS.UPDATE(id), payload)
    );
  },

  /**
   * Delete configuration (soft delete, owner only)
   */
  deleteConfig: async (id) => {
    return apiRequest(() =>
      axiosInstance.delete(COLUMN_CONFIGURATION_ENDPOINTS.DELETE(id))
    );
  },

  /**
   * Set configuration as user's default
   * Auto-duplicates shared configs before setting
   */
  setAsDefault: async (id) => {
    return apiRequest(() =>
      axiosInstance.post(COLUMN_CONFIGURATION_ENDPOINTS.SET_DEFAULT(id))
    );
  },

  /**
   * Toggle share status (owner only)
   */
  toggleShare: async (id) => {
    return apiRequest(() =>
      axiosInstance.post(COLUMN_CONFIGURATION_ENDPOINTS.TOGGLE_SHARE(id))
    );
  },

  /**
   * Duplicate configuration
   */
  duplicateConfig: async (id, newName) => {
    if (!newName || newName.trim().length === 0) {
      throw new Error('New name is required for duplicate configuration');
    }
    if (newName.length > 100) {
      throw new Error('Configuration name must not exceed 100 characters');
    }

    // Send newName as query parameter, not body
    return apiRequest(() =>
      axiosInstance.post(
        `${COLUMN_CONFIGURATION_ENDPOINTS.DUPLICATE(id)}?newName=${encodeURIComponent(newName)}`
      )
    );
  },

  /**
   * Validate configuration name
   */
  validateConfigName: (name, existingConfigs = []) => {
    const errors = [];

    if (!name || name.trim().length === 0) {
      errors.push('Configuration name is required');
    } else if (name.length > 100) {
      errors.push('Configuration name must not exceed 100 characters');
    }

    // Check for duplicate names
    const duplicate = existingConfigs.find(
      c => c.name.toLowerCase() === name.trim().toLowerCase()
    );
    if (duplicate) {
      errors.push('A configuration with this name already exists');
    }

    return { isValid: errors.length === 0, errors };
  },
};

export default columnConfigurationService;
